import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react'

import { useAuth } from 'src/auth'
import { usePhoneDeviceDispatch } from 'src/context/PhoneDeviceContext'
import { RoleType } from 'src/graphql/types/employees'
import { PhoneDeviceActionType } from 'src/lib/phone-device.reducer'
const WebSocketsContext = createContext({ newNotification: null })
const WebSocketsDispatch = createContext(null)
import {
  applyRealtimeToken,
  subscribeToNotifications,
  subscribeToCallChannel,
  unsubscribeAll,
} from 'src/lib/websockets'

import { WebSocketsActionType, websocketsReducer } from './websockets.reducer'

const REALTIME_ROLES = [RoleType.RECRUITER, RoleType.ADMIN, RoleType.OPS]

/**
 * How often to re-apply the Supabase JWT to the live socket.
 *
 * We subscribe once per mount, so the token captured then would otherwise be
 * the only one Realtime ever sees. Once it expires, the rejoin that follows any
 * network blip is rejected and the channel retries forever. Auth0 serves this
 * from cache until the token is close to expiry, so the poll is cheap.
 */
const TOKEN_REFRESH_INTERVAL_MS = 10 * 60 * 1000

export const WebSocketsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(websocketsReducer, {
    newNotification: null,
  })

  const phoneDispatch = usePhoneDeviceDispatch()

  const { client, currentUser } = useAuth()

  // Held in a ref so the subscribe effect never has to re-run (and therefore
  // never has to re-open a channel) just because a dispatch identity changed.
  const phoneDispatchRef = useRef(phoneDispatch)
  phoneDispatchRef.current = phoneDispatch

  const hasRealtimeRole = REALTIME_ROLES.some((role) =>
    currentUser?.roles?.includes(role)
  )

  useEffect(() => {
    if (!client || !hasRealtimeRole) {
      return
    }

    // Guards the async gap below: if the provider unmounts (or the effect
    // re-runs) before getUser() resolves, we must not open channels that the
    // cleanup has already run past.
    let cancelled = false

    const subscribe = async () => {
      try {
        const auth0User = await client.getUser()
        if (!auth0User || cancelled) {
          return
        }

        const credentials = {
          supabaseToken: auth0User.supabaseAccessToken,
          authId: auth0User.sub,
        }

        await subscribeToNotifications({
          ...credentials,
          onMessage: (message) => {
            dispatch({
              type: WebSocketsActionType.NEW_NOTIFICATION,
              payload: message,
            })
          },
        })

        if (cancelled) {
          return
        }

        await subscribeToCallChannel({
          ...credentials,
          onMessage: (message: {
            new?: { callStatus?: string; activityLogId?: string }
          }) => {
            if (message?.new?.callStatus) {
              phoneDispatchRef.current?.({
                type: PhoneDeviceActionType.SET_CALL_STATE,
                payload: {
                  callState: message?.new?.callStatus,
                  activityLogId: message?.new?.activityLogId,
                },
              })
            }
          },
        })
      } catch (error) {
        // Gracefully handle environments where localStorage is restricted
        // (e.g., in-app browsers, private browsing mode), which causes
        // Auth0's client.getUser() to throw a SecurityError.
        if (error instanceof DOMException && error.name === 'SecurityError') {
          console.warn(
            'WebSocket subscription failed: localStorage access restricted'
          )
          return
        }
        throw error
      }
    }

    // The effect runs once per mount, so the JWT handed to Realtime at
    // subscribe time would go stale while the tab stays open. Re-apply a fresh
    // one on a timer; `setAuth` also pushes it to the already-joined channels.
    const refreshRealtimeToken = async () => {
      try {
        // Triggers a silent renewal when the cached token is near expiry,
        // which is what refreshes the custom claim read below.
        await client.getTokenSilently()
        const auth0User = await client.getUser()

        if (cancelled || !auth0User?.supabaseAccessToken) {
          return
        }

        applyRealtimeToken(auth0User.supabaseAccessToken)
      } catch (error) {
        console.warn('Failed to refresh Supabase realtime token', error)
      }
    }

    // Bare `subscribe()` would turn the re-throw above into an unhandled
    // rejection, so nothing would ever report a failed getUser().
    subscribe().catch((error) => {
      console.error('Supabase realtime subscription failed', error)
    })

    const refreshInterval = setInterval(
      refreshRealtimeToken,
      TOKEN_REFRESH_INTERVAL_MS
    )

    return () => {
      cancelled = true
      clearInterval(refreshInterval)
      // Closes the channels and, with them, the websocket — otherwise the
      // connection stays counted against Supabase's concurrent-connection
      // quota for as long as the tab is open.
      unsubscribeAll()
    }
  }, [client, hasRealtimeRole])

  return (
    <WebSocketsContext.Provider value={state}>
      <WebSocketsDispatch.Provider value={dispatch}>
        {children}
      </WebSocketsDispatch.Provider>
    </WebSocketsContext.Provider>
  )
}

export function useWebsockets() {
  return useContext(WebSocketsContext)
}

export function useWebsocketsDispatch() {
  return useContext(WebSocketsDispatch)
}
